import { clsx } from "clsx";
import { ArrowRight, ChevronDown, Clock, Scissors } from "lucide-react";
import { Link } from "react-router";
import type { PublicStylist, Service } from "@/api/contract";
import { faqs } from "@/content";
import { duration, money } from "@/lib/format";
import { Card } from "./ui";

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function Avatar({ name, color, photoUrl, size = "md" }: { name: string; color: string; photoUrl?: string; size?: "sm" | "md" | "lg" | "xl" }) {
  const cls = { sm: "size-9 text-sm", md: "size-12 text-base", lg: "size-20 text-2xl", xl: "size-40 text-5xl" }[size];
  if (photoUrl) return <img src={photoUrl} alt="" className={clsx(cls, "rounded-full object-cover")} />;
  return (
    <span className={clsx(cls, "grid shrink-0 place-items-center rounded-full font-display text-white")} style={{ background: color }} aria-hidden>
      {initials(name)}
    </span>
  );
}

export function ServiceRow({ service }: { service: Service }) {
  return (
    <li className="group flex items-start justify-between gap-4 py-5">
      <div className="min-w-0">
        <h3 className="font-semibold text-ink">{service.name}</h3>
        <p className="mt-1 text-sm text-muted">{service.description}</p>
        <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-steel">
          <Clock className="size-3.5" aria-hidden /> {duration(service.durationMinutes)}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2">
        <span className="font-display text-2xl text-rust">{money(service.priceCents, service.priceFrom)}</span>
        <Link
          to={`/book?service=${service.id}`}
          className="inline-flex items-center gap-1 rounded-full bg-rust-50 px-3 py-1.5 text-sm font-semibold text-rust transition group-hover:bg-rust group-hover:text-white"
          aria-label={`Book ${service.name}`}
        >
          Book <ArrowRight className="size-3.5" aria-hidden />
        </Link>
      </div>
    </li>
  );
}

export function ServiceMenu({ services, limit }: { services: Service[]; limit?: number }) {
  const shown = limit ? services.slice(0, limit) : services;
  const groups = [...new Set(shown.map((s) => s.category))];
  return (
    <div className="grid gap-6 md:grid-cols-2">
      {groups.map((g) => (
        <Card key={g} className="p-6 sm:p-8">
          <h3 className="flex items-center gap-2 eyebrow text-steel">
            <Scissors className="size-4 text-rust" aria-hidden /> {g}
          </h3>
          <ul className="divide-y divide-line">
            {shown
              .filter((s) => s.category === g)
              .map((s) => (
                <ServiceRow key={s.id} service={s} />
              ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}

export function StylistCard({ stylist }: { stylist: PublicStylist }) {
  return (
    <Card className="group flex flex-col p-6 transition hover:-translate-y-1 hover:shadow-lift">
      <div className="flex items-center gap-4">
        <Avatar name={stylist.name} color={stylist.color} photoUrl={stylist.photoUrl} size="lg" />
        <div>
          <h3 className="font-display text-2xl">{stylist.name}</h3>
          <p className="eyebrow mt-1 text-[0.7rem] text-steel">{stylist.title}</p>
        </div>
      </div>
      <p className="mt-4 line-clamp-3 text-muted">{stylist.bio}</p>
      <div className="mt-6 flex items-center gap-4 pt-2 text-sm font-semibold">
        <Link to={`/team/${stylist.id}`} className="text-ink hover:text-rust">
          Meet {stylist.name.split(" ")[0]}
        </Link>
        {stylist.bookable && (
          <Link to={`/book?stylist=${stylist.id}`} className="ml-auto inline-flex items-center gap-1 text-rust">
            Book <ArrowRight className="size-4" aria-hidden />
          </Link>
        )}
      </div>
    </Card>
  );
}

export function Faq() {
  return (
    <div className="grid gap-10 md:grid-cols-3">
      {faqs.map((group) => (
        <div key={group.group}>
          <h3 className="eyebrow text-steel">{group.group}</h3>
          <div className="mt-4 divide-y divide-line rounded-[var(--radius-card)] bg-white ring-1 ring-line">
            {group.items.map((item) => (
              <details key={item.q} className="group px-5 py-4 [&_summary::-webkit-details-marker]:hidden">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-3 font-semibold">
                  {item.q}
                  <ChevronDown className="mt-0.5 size-5 shrink-0 text-rust transition group-open:rotate-180" aria-hidden />
                </summary>
                <p className="mt-3 text-muted">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Stand-in for salon photography until real photos are supplied. */
export function PhotoTile({ label, className, tone = "rust" }: { label: string; className?: string; tone?: "rust" | "sand" | "ink" }) {
  const bg = {
    rust: "bg-[radial-gradient(circle_at_30%_20%,#c0563f,#9a3324_55%,#5f2017)] text-white/90",
    sand: "bg-[radial-gradient(circle_at_70%_30%,#fff,#f2ece6_60%,#e7e0d9)] text-muted",
    ink: "bg-[radial-gradient(circle_at_40%_30%,#4a4a4a,#1f1f1f_70%)] text-white/80",
  }[tone];
  return (
    <div className={clsx("relative overflow-hidden rounded-[var(--radius-card)]", bg, className)} role="img" aria-label={label}>
      <Scissors className="absolute -right-6 -bottom-6 size-40 opacity-10" aria-hidden />
      <span className="absolute bottom-4 left-4 eyebrow text-[0.65rem] opacity-80">{label}</span>
    </div>
  );
}
